import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';
import Icon from '../Shared/Icons';
import { RATING_LABELS } from '../../utils/reportSchema';

// ── Design tokens (zinaendana na muundo uliopo wa Foundation) ──────────
export const T = {
  bg: '#F6F4EE',
  card: '#FFFFFF',
  green: '#14452F',
  greenLight: '#1F6B45',
  orange: '#E07A2E',
  ink: '#1A1A1A',
  muted: '#6B7280',
  line: '#E8E4DA',
  serif: "'Fraunces', Georgia, serif",
  sans: "'Plus Jakarta Sans', -apple-system, sans-serif",
};

export const S = {
  layout: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: T.sans },
  main: { flex: 1, padding: '26px 20px 60px', overflowY: 'auto', width: '100%', maxWidth: '100%' },
  card: {
    background: T.card, borderRadius: '16px', padding: '20px',
    border: `1px solid ${T.line}`, marginBottom: '16px',
  },
  h1: { fontFamily: T.serif, fontSize: '25px', color: T.green, margin: 0, lineHeight: 1.2 },
  h2: { fontFamily: T.serif, fontSize: '18px', color: T.green, margin: '0 0 12px' },
  sub: { fontSize: '13px', color: T.muted, lineHeight: 1.6 },
  label: { display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '11px 13px', borderRadius: '10px',
    border: `1px solid ${T.line}`, fontSize: '14px', fontFamily: 'inherit',
    background: '#FCFBF8', color: T.ink, boxSizing: 'border-box', outline: 'none',
  },
  btn: {
    background: `linear-gradient(135deg, ${T.green}, ${T.greenLight})`, color: '#fff',
    border: 'none', padding: '13px 22px', borderRadius: '11px', fontSize: '14px',
    fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  btnGhost: {
    background: '#fff', color: T.green, border: `1px solid ${T.line}`,
    padding: '11px 18px', borderRadius: '11px', fontSize: '13.5px',
    fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px',
    borderRadius: '99px', fontSize: '11px', fontWeight: 700,
  },
  empty: { textAlign: 'center', padding: '40px 20px', color: T.muted, fontSize: '13.5px' },
  err: { background: '#FEF2F2', color: '#991B1B', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' },
  ok: { background: '#ECFDF5', color: '#065F46', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' },
};

export function Pill({ children, color = T.green, bg }) {
  return (
    <span style={{ ...S.pill, color, background: bg || `${color}15` }}>{children}</span>
  );
}

// ── Kichwa cha programu + tabs ─────────────────────────────────────────
export function ProgramShell({ program, children, tab }) {
  const navigate = useNavigate();
  const location = useLocation();
  const tabs = [
    { key: 'home',    label: 'Muhtasari', path: `/programs/${program.id}` },
    { key: 'members', label: 'Wanachama', path: `/programs/${program.id}/members` },
    { key: 'week',    label: 'Bodi ya Wiki', path: `/programs/${program.id}/week` },
    { key: 'session', label: 'Session', path: `/programs/${program.id}/session` },
    { key: 'clubs',   label: 'Vilabu', path: `/programs/${program.id}/clubs` },
    { key: 'ai',      label: 'AI', path: `/programs/${program.id}/ai` },
  ];
  const active = tab || tabs.find(t => t.path === location.pathname)?.key || 'home';

  return (
    <div style={S.layout}>
      <Sidebar />
      <div style={S.main}>
        <div style={{
          background: `linear-gradient(135deg, ${program.color}, ${program.accent})`,
          borderRadius: '18px', padding: '22px 20px', color: '#fff', marginBottom: '16px',
        }}>
          <div
            onClick={() => navigate('/programs')}
            style={{ fontSize: '12px', opacity: 0.8, cursor: 'pointer', marginBottom: '8px' }}
          >
            ← Programu zote
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <Icon name={program.icon} size={26} color="#fff" />
            <div>
              <div style={{ fontFamily: T.serif, fontSize: '21px', lineHeight: 1.2 }}>{program.name}</div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '3px' }}>{program.tagline}</div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', gap: '7px', overflowX: 'auto', paddingBottom: '4px',
          marginBottom: '16px', WebkitOverflowScrolling: 'touch',
        }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => navigate(t.path)}
              style={{
                whiteSpace: 'nowrap', padding: '9px 15px', borderRadius: '99px',
                border: `1px solid ${active === t.key ? program.color : T.line}`,
                background: active === t.key ? program.color : '#fff',
                color: active === t.key ? '#fff' : T.muted,
                fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {children}
      </div>
    </div>
  );
}

// ── Field renderer — hii ndio inayofanya fomu zijijenge zenyewe ────────
export function Field({ field, value, onChange, color = T.green }) {
  const set = v => onChange(field.key, v);

  if (field.type === 'rating') {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={S.label}>
          {field.label}
          {value ? <span style={{ color, fontWeight: 600 }}> — {RATING_LABELS[value]}</span> : null}
        </label>
        <div style={{ display: 'flex', gap: '7px' }}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => set(value === n ? '' : n)}
              style={{
                flex: 1, padding: '10px 0', borderRadius: '10px', cursor: 'pointer',
                border: `1px solid ${value === n ? color : T.line}`,
                background: value === n ? color : '#FCFBF8',
                color: value === n ? '#fff' : T.muted,
                fontSize: '14px', fontWeight: 700, fontFamily: 'inherit',
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={S.label}>{field.label}{field.required && <span style={{ color: '#DC2626' }}> *</span>}</label>
        <select style={S.input} value={value || ''} onChange={e => set(e.target.value)}>
          <option value="">— Chagua —</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={S.label}>{field.label}{field.required && <span style={{ color: '#DC2626' }}> *</span>}</label>
        <textarea
          style={{ ...S.input, minHeight: `${(field.rows || 3) * 26}px`, resize: 'vertical', lineHeight: 1.6 }}
          value={value || ''}
          placeholder={field.placeholder || ''}
          onChange={e => set(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === 'check') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px', cursor: 'pointer', fontSize: '13.5px', color: '#374151' }}>
        <input type="checkbox" checked={!!value} onChange={e => set(e.target.checked)} style={{ width: '17px', height: '17px' }} />
        {field.label}
      </label>
    );
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={S.label}>{field.label}{field.required && <span style={{ color: '#DC2626' }}> *</span>}</label>
      <input
        style={S.input}
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        min={field.min}
        max={field.max}
        value={value === undefined || value === null ? '' : value}
        placeholder={field.placeholder || ''}
        onChange={e => set(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      />
    </div>
  );
}

// ── Kikundi cha bloku kinachoweza kufunguliwa/kufungwa ────────────────
export function BlockSection({ block, open, onToggle, done, children }) {
  return (
    <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '15px 18px',
          cursor: 'pointer', background: open ? `${block.color}0A` : '#fff',
          borderBottom: open ? `1px solid ${T.line}` : 'none',
        }}
      >
        <div style={{
          width: '32px', height: '32px', borderRadius: '9px', background: `${block.color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={block.icon} size={16} color={block.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: T.ink }}>{block.label}</div>
          {block.shared && (
            <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '2px' }}>
              Maswali yaleyale kwa programu zote
            </div>
          )}
        </div>
        <div style={{ fontSize: '11.5px', fontWeight: 700, color: done === 100 ? '#059669' : T.muted }}>
          {done}%
        </div>
        <span style={{ color: T.muted, fontSize: '13px', transform: open ? 'rotate(90deg)' : 'none', transition: '0.15s' }}>›</span>
      </div>
      {open && <div style={{ padding: '18px' }}>{children}</div>}
    </div>
  );
}
